import { Athlete } from "types/athlete";
import CsvHelpers from "helpers/CsvHelpers/CsvHelpers";
import { CsvColumn } from "helpers/CsvHelpers/CsvHelpers.types";
import uniqueId from "lodash/uniqueId";
import moment from "moment";
import datetime from "config/datetime";
import { AthleteBase } from "types/athlete";
import GenderHelpers from "helpers/GenderHelpers";
import * as Yup from "yup";
import { regexEmail } from "config/regex";
import { FieldOption, BaseFieldMapping } from "types/fieldMapping";

export const EmailStatus = {
  Sent: "Sent",
  Joined: "Joined",
} as const;

export type EmailStatusType = (typeof EmailStatus)[keyof typeof EmailStatus];

class AthleteHelpers {
  static getFullName(athlete: Pick<Athlete, "firstName" | "lastName">) {
    return [athlete.firstName, athlete.lastName].filter(Boolean).join(" ");
  }

  static getProfileImagePath(athlete?: Pick<Athlete, "id" | "profilePhotoName">) {
    return athlete?.profilePhotoName && `athletes/${athlete.id}/${athlete.profilePhotoName}`;
  }

  static getAge(dateOfBirth: string | null): number | null {
    if (!dateOfBirth) return null;
    return datetime().diff(datetime(dateOfBirth), "year");
  }

  static getInitials(athlete: Pick<Athlete, "firstName" | "lastName">) {
    return AthleteHelpers.getFullName(athlete)
      .split(" ")
      .slice(0, 2)
      .map((name) => name[0])
      .join("");
  }

  static getColumns(options?: {
    imperialSystemEnabled?: boolean;
    canAccessGroups?: boolean;
  }): CsvColumn<Partial<Record<keyof AthleteBase, string>>>[] {
    const { imperialSystemEnabled = false, canAccessGroups } = options || {};

    const csvColumns: CsvColumn<Partial<Record<keyof AthleteBase, string>>>[] = [
      {
        label: "First Name",
        key: "firstName",
      },
      {
        label: "Last Name",
        key: "lastName",
      },
      {
        label: "External ID",
        key: "externalId",
      },
      {
        label: "Email Address",
        key: "email",
      },
      {
        label: "Sex (Male/Female/Prefer Not Say/[or leave field blank])",
        key: "sex",
      },
      {
        label: "Date of Birth (DD/MM/YYYY)",
        key: "dateOfBirth",
      },
      {
        label: `Height ${imperialSystemEnabled ? `(ft'in")` : "(cm)"}`,
        key: "height",
      },
      {
        label: `Weight ${imperialSystemEnabled ? "(lbs)" : "(kg)"}`,
        key: "weight",
      },
    ];

    if (canAccessGroups) {
      const groupColumn: CsvColumn<Partial<Record<keyof AthleteBase, string>>> = {
        label: "Groups",
        key: "groupIds",
      };
      csvColumns.push(groupColumn);
    }

    return csvColumns;
  }

  static generateBulkUploadCsvURI(options?: { imperialSystemEnabled?: boolean; canAccessGroups?: boolean }) {
    const { imperialSystemEnabled = false, canAccessGroups } = options || {};
    const columns = AthleteHelpers.getColumns({ imperialSystemEnabled, canAccessGroups });

    const exampleAthlete: Partial<Record<keyof AthleteBase, string>> = {
      firstName: "John",
      lastName: "Doe",
      externalId: "optional_external_id_1000",
      email: "john.doe@example.com",
      sex: "",
      dateOfBirth: "31/12/1990",
      height: `${imperialSystemEnabled ? `5'11"` : "182"}`,
      weight: `${imperialSystemEnabled ? "190" : "86"}`,
    };

    if (canAccessGroups) {
      exampleAthlete.groupIds = "Group Name 1;Group Name 2;Group Name 3;Group Name 4";
    }

    return CsvHelpers.buildURI(columns, [exampleAthlete]);
  }

  static async csvFileToJson(csvData: File, options?: { imperialSystemEnabled?: boolean; canAccessGroups?: boolean }) {
    const columns = AthleteHelpers.getColumns(options);

    const arrayOfArrays: string[][] = await CsvHelpers.readFileAsText(csvData);

    const csvHeaders = arrayOfArrays.shift();
    const expectedColumnCount = columns.length;

    // validateHeadings
    csvHeaders!.forEach((header, index) => {
      if (header.replace(/"/g, "") !== columns[index].label.replace(/"/g, "")) {
        // if the error occurs in the height or weight column
        // it's a measurement system mismatch
        if (
          (header.includes("Height") && columns[index].label.includes("Height")) ||
          (header.includes("Weight") && columns[index].label.includes("Weight"))
        ) {
          throw new Error("measurement-system-mismatch");
        }

        throw new Error("column-heading-mismatch");
      }
    });

    const propertyKeys = columns.map((column) => column.key);

    // Filter out empty rows first, then validate column count
    const nonEmptyRows = arrayOfArrays.filter((row) => row.filter(Boolean).length > 0);

    // validateColumnCount - check each non-empty row has the correct number of columns
    nonEmptyRows.forEach((row, index) => {
      if (row.length !== expectedColumnCount) {
        throw new Error("incorrect-column-count");
      }
    });

    // validateEmailFormat - use Yup schema to validate email formats
    const emailColumnIndex = columns.findIndex((col) => col.key === "email");
    if (emailColumnIndex !== -1) {
      const emailSchema = Yup.string().email().matches(regexEmail);

      const hasInvalidEmails = nonEmptyRows.some((row) => {
        const email = row[emailColumnIndex]?.trim();
        if (!email) return false; // Empty emails are okay

        try {
          emailSchema.validateSync(email);
          return false; // Valid email
        } catch (error) {
          return true; // Invalid email
        }
      });

      if (hasInvalidEmails) {
        throw new Error("invalid-email-format");
      }
    }

    const baseAthletes = nonEmptyRows.map((row) => {
      const stringAthlete: Record<keyof AthleteBase, string> = propertyKeys.reduce(
        (acc, key, index) => ({ ...acc, [key]: row[index] }),
        {
          id: "",
          firstName: "",
          lastName: "",
          externalId: "",
          email: "",
          sex: "",
          dateOfBirth: "",
          height: "",
          weight: "",
          groupIds: "",
        }
      );

      /**
       * Since we only accept Male or Female, we need to
       * make sure that we check for the correct value
       */
      const athleteSex = GenderHelpers.gendersAccepted.find(
        (gender) => gender.toLowerCase() === stringAthlete.sex.trim().toLowerCase()
      );

      const baseAthlete: AthleteBase = {
        id: uniqueId(),
        firstName: stringAthlete.firstName?.trim(),
        lastName: stringAthlete.lastName?.trim(),
        externalId: stringAthlete.externalId?.trim() || null,
        email: stringAthlete.email?.trim().toLowerCase() || null,
        sex: athleteSex || null,
        dateOfBirth: stringAthlete.dateOfBirth?.trim()
          ? moment(stringAthlete.dateOfBirth?.trim(), "DD/MM/YYYY").toISOString()
          : null,
        height:
          Number(stringAthlete.height) ||
          (stringAthlete.height.trim()
            ? stringAthlete.height?.replace(/\s/g, "").replace(/"/g, "") + `"` // "5'11""" => 5'11"
            : null),
        weight: Number(stringAthlete.weight) || null,
        groupIds: stringAthlete.groupIds
          .replace(/,/g, ";") // Replace commas with semicolons for group separation
          .split(";")
          .map((groupName) => groupName.trim())
          .filter(Boolean),
      };

      return baseAthlete;
    });

    return baseAthletes;
  }

  /**
   * Get field options for athlete field mapping
   */
  static getFieldOptions(canAccessGroups: boolean = false): FieldOption[] {
    const baseOptions: FieldOption[] = [
      { value: "firstName", label: "First Name", isRequired: true },
      { value: "lastName", label: "Last Name", isRequired: true },
      { value: "externalId", label: "External ID" },
      { value: "email", label: "Email Address" },
      { value: "sex", label: "Sex" },
      { value: "dateOfBirth", label: "Date of Birth" },
      { value: "height", label: "Height", description: "Numeric or imperial format" },
      { value: "weight", label: "Weight", description: "Numeric only" },
    ];

    // Only add groups option if user has permission
    if (canAccessGroups) {
      baseOptions.push({ value: "groupIds", label: "Groups" });
    }

    return baseOptions;
  }

  /**
   * Reprocess athletes from raw CSV content using field mappings
   */
  static reprocessWithMappings(
    fileContent: string,
    mappings: BaseFieldMapping[],
    options?: {
      imperialSystemEnabled?: boolean;
      canAccessGroups?: boolean;
    }
  ): AthleteBase[] {
    const { canAccessGroups = false } = options || {};

    try {
      const lines = fileContent.split("\n").filter((line) => line.trim() !== "");

      if (lines.length < 2) {
        throw new Error("File must contain at least a header row and one data row");
      }

      // Parse header
      const headerLine = lines[0];
      const headers = headerLine.split(",").map((header) => header.trim().replace(/["']/g, ""));

      // Create column mapping from field mappings
      const columnMapping: Record<string, keyof AthleteBase> = {};
      mappings.forEach((mapping) => {
        const columnIndex = headers.findIndex((header) => header.toLowerCase() === mapping.sourceField.toLowerCase());
        if (columnIndex !== -1) {
          columnMapping[headers[columnIndex]] = mapping.targetField as keyof AthleteBase;
        }
      });

      // Process data rows
      const athletes = lines
        .slice(1)
        .map((line, index) => {
          const values = line.split(",").map((value) => value.trim().replace(/["']/g, ""));
          const rowData: Record<string, string | null> = {};

          // Map values to column headers
          headers.forEach((header, i) => {
            rowData[header] = values[i] || null;
          });

          // Create athlete object
          const athlete: Partial<AthleteBase> = {
            id: uniqueId(),
            firstName: "",
            lastName: "",
            groupIds: [],
          };

          // Apply mappings
          Object.entries(columnMapping).forEach(([sourceColumn, targetField]) => {
            const value = rowData[sourceColumn];
            if (value !== null && value !== undefined && value !== "") {
              switch (targetField) {
                case "firstName":
                case "lastName":
                case "externalId":
                case "email":
                case "sex":
                  athlete[targetField] = String(value).trim();
                  break;
                case "dateOfBirth":
                  try {
                    const parsedDate = moment(String(value).trim(), ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"]);
                    if (parsedDate.isValid()) {
                      athlete.dateOfBirth = parsedDate.toISOString();
                    }
                  } catch (error) {
                    console.warn(`Could not parse date: ${value}`);
                  }
                  break;
                case "height":
                  const heightValue = String(value).trim();
                  if (heightValue) {
                    const numericHeight = parseFloat(heightValue);
                    if (!isNaN(numericHeight)) {
                      athlete.height = numericHeight;
                    } else if (heightValue.includes("'") || heightValue.includes('"')) {
                      // Imperial format - keep as string
                      athlete.height = heightValue.replace(/\s/g, "").replace(/"/g, "") + '"';
                    }
                  }
                  break;
                case "weight":
                  const weightValue = parseFloat(String(value).trim());
                  if (!isNaN(weightValue)) {
                    athlete.weight = weightValue;
                  }
                  break;
                case "groupIds":
                  if (canAccessGroups && String(value).trim()) {
                    athlete.groupIds = String(value)
                      .split(/[;,]/) // Split by semicolon or comma
                      .map((group) => group.trim())
                      .filter((group) => group.length > 0);
                  }
                  break;
              }
            }
          });

          // Validate required fields
          if (!athlete.firstName || !athlete.lastName) {
            console.warn(`Skipping row ${index + 1}: Missing required firstName or lastName`, {
              firstName: athlete.firstName,
              lastName: athlete.lastName,
              rowData: rowData,
            });
            return null;
          }

          // Ensure groupIds is always an array
          if (!athlete.groupIds) {
            athlete.groupIds = [];
          }

          return athlete as AthleteBase;
        })
        .filter((athlete): athlete is AthleteBase => athlete !== null);

      return athletes;
    } catch (error) {
      console.error("Error reprocessing athletes with mappings:", error);
      throw error;
    }
  }

  /**
   * The address to display for an athlete. An unconfirmed (pending) address
   * takes precedence over the confirmed one, as it is the most recent intent.
   */
  static getAthleteEmail(athlete: Pick<Athlete, "email" | "pendingEmail">): string {
    return athlete.pendingEmail ?? athlete.email ?? "";
  }

  static getAthleteEmailStatus(athlete: Pick<Athlete, "email" | "pendingEmail">): EmailStatusType | null {
    if (athlete.pendingEmail) {
      return EmailStatus.Sent;
    }
    if (athlete.email) {
      return EmailStatus.Joined;
    }
    return null;
  }
}

export default AthleteHelpers;
